import axios, { AxiosInstance } from 'axios';
import { useEffect, useRef } from 'react';
import { useUser } from '../contexts/UserContext';
import { config } from '@/config';
import { TokensClient, RefreshTokenRequest } from '../rest/apiv2.hsdesk';
import {
  getStoredTenantId,
  getTenantRequestHeaders,
  setStoredTenantId
} from '@/feature/auth/utils/tenantStorage';
import { redirectToLoginWithReturnTo } from '@/feature/auth/utils/authRouting';

export const useAxiosWithAuth = (): AxiosInstance => {
  const { accessToken, setAccessToken, setPermissions, setUser } = useUser();
  const axiosInstanceRef = useRef<AxiosInstance | null>(null);

  const requestInterceptorId = useRef<number | null>(null);
  const responseInterceptorId = useRef<number | null>(null);

  if (!axiosInstanceRef.current) {
    axiosInstanceRef.current = axios.create({
      baseURL: config.apiUrl,
      withCredentials: true
    });
  }

  useEffect(() => {
    const axiosInstance = axiosInstanceRef.current;
    if (!axiosInstance) return;

    // Usuń stare interceptory (jeśli istnieją)
    if (requestInterceptorId.current !== null) {
      axiosInstance.interceptors.request.eject(requestInterceptorId.current);
    }

    if (responseInterceptorId.current !== null) {
      axiosInstance.interceptors.response.eject(responseInterceptorId.current);
    }

    const tokensClient = new TokensClient(config.apiUrl, axiosInstance);
    const clearExpiredSession = () => {
      setAccessToken(null);
      setPermissions(null);
      setUser(null);
      redirectToLoginWithReturnTo();
    };

    // 🔐 Request interceptor z aktualnym tokenem
    requestInterceptorId.current = axiosInstance.interceptors.request.use(
      (config) => {
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        Object.assign(config.headers, getTenantRequestHeaders());
        return config;
      }
    );

    // 🔁 Response interceptor (odświeżanie tokena)
    responseInterceptorId.current = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const requestUrl = String(originalRequest?.url ?? '');
        const isRefreshRequest = requestUrl.includes('/api/tokens/refresh');

        if (error.response?.status === 401 && isRefreshRequest) {
          clearExpiredSession();
          return Promise.reject(error);
        }

        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          try {
            const refreshReq = new RefreshTokenRequest({
              token: accessToken || undefined,
              refreshToken: sessionStorage.getItem('refreshToken') || undefined
            });

            const tenantId = getStoredTenantId();
            const refreshed = await tokensClient.refresh(tenantId, refreshReq);

            setAccessToken(refreshed.token ?? null);
            setStoredTenantId(tenantId);
            sessionStorage.setItem('accessToken', refreshed.token || '');
            sessionStorage.setItem(
              'refreshToken',
              refreshed.refreshToken || ''
            );

            // 🔁 Retry z nowym tokenem
            originalRequest.headers.Authorization = `Bearer ${refreshed.token}`;
            Object.assign(originalRequest.headers, getTenantRequestHeaders());
            return axiosInstance(originalRequest);
          } catch (refreshErr) {
            clearExpiredSession();
            return Promise.reject(refreshErr);
          }
        }

        return Promise.reject(error);
      }
    );
  }, [accessToken, setAccessToken, setPermissions, setUser]);

  return axiosInstanceRef.current!;
};
